import axios from "axios";
import { REACT_APP_BASE_PATH } from "../config/runtimeConfig";

export type DeploymentMode = {
  mode: "default" | "onprem";
  onPrem: boolean;
  allowedEmailDomains: string[];
  emailVerificationRequired: boolean;
  /** VPN profiles for data adaptors / OpenAPI catalogs are available on this deployment. */
  vpnEnabled: boolean;
};

export const DEFAULT_DEPLOYMENT_MODE: DeploymentMode = {
  mode: "default",
  onPrem: false,
  allowedEmailDomains: [],
  emailVerificationRequired: true,
  vpnEnabled: false,
};

const publicApi = axios.create({
  baseURL: REACT_APP_BASE_PATH,
  headers: { "Content-Type": "application/json" },
});

let cached: DeploymentMode | null = null;
let pending: Promise<DeploymentMode> | null = null;

/** Whether an e-mail address belongs to one of the allowed domains (exact, or `*.domain` for sub-domains). */
export function isEmailDomainAllowed(email: string, domains: string[]): boolean {
  const value = email.trim().toLowerCase();
  const at = value.lastIndexOf("@");
  if (at <= 0 || at === value.length - 1) return false;
  const domain = value.slice(at + 1);
  return domains.some((raw) => {
    const allowed = raw.trim().toLowerCase();
    if (allowed.startsWith("*.")) {
      const suffix = allowed.slice(1);
      return domain.endsWith(suffix) && domain.length > suffix.length;
    }
    return domain === allowed;
  });
}

export const deploymentModeService = {
  /** Public endpoint; cached for the page lifetime. Falls back to the default mode when unreachable. */
  get: (): Promise<DeploymentMode> => {
    if (cached) return Promise.resolve(cached);
    if (!pending) {
      pending = publicApi
        .get("/public/config/mode")
        .then((res) => {
          const data = res.data ?? {};
          const onPrem = data.mode === "onprem";
          cached = {
            mode: onPrem ? "onprem" : "default",
            onPrem,
            allowedEmailDomains: Array.isArray(data.allowedEmailDomains) ? data.allowedEmailDomains : [],
            emailVerificationRequired: data.emailVerificationRequired !== false,
            vpnEnabled: data.vpnEnabled === true,
          };
          return cached;
        })
        .catch(() => DEFAULT_DEPLOYMENT_MODE)
        .finally(() => {
          pending = null;
        });
    }
    return pending;
  },
  /** Synchronous read for code that cannot await (null until the first load finished). */
  peek: (): DeploymentMode | null => cached,
};
